import { COLORS, ROUTE_LOADS } from '@/common/constants';
import { ISidebarItem } from '@/common/models/ISidebar';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams, useSearchParams } from 'react-router-dom';

interface ChildViewProps {
  item: ISidebarItem;
}

export const ChildView = ({ item }: ChildViewProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { id, status } = useParams<{ id: string; status: string }>();
  const isActive = id === item?.id;
  const statusColor = COLORS.find((c) => c.key === item?.current_status);

  const navTo = {
    pathname: `${ROUTE_LOADS}/${status || 'ALL'}/${item.id}`,
    search: `?${searchParams.toString()}`,
  };

  const cell = (label: string, value: string | number | undefined) => (
    <div className="flex flex-col items-start">
      <p className="text-[11px] font-medium text-gray-graphite">{label}</p>
      <p className="text-sm text-midnight-default line-clamp-1">
        {value || '-'}
      </p>
    </div>
  );

  return (
    <NavLink
      to={navTo}
      className={cn(
        'relative grid grid-cols-12 gap-2.5 border-b px-6 py-2 transition-all duration-300 cursor-pointer',
        'pl-10 bg-gray-200 border-l-2 border-dashed border-green-200',
        isActive && 'bg-green-50',
      )}
    >
      {statusColor && (
        <Badge
          className={cn(
            'absolute top-1 right-3 text-[10px] py-[1px] px-2 text-white rounded-md',
            statusColor.color,
          )}
        >
          {t(statusColor.name)}
        </Badge>
      )}

      <span
        className={cn(
          'absolute left-0 inset-y-0 w-1 opacity-0 transition-all duration-300',
        )}
      />

      <div className="col-span-10 flex items-center gap-4">
        {cell(t('organization.stir'), item.tax_number)}

        <div className="flex flex-col items-start">
          <p className="text-[10px] font-medium">{t('organization.name')}</p>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="w-[calc(440px-160px)] text-sm font-medium text-dark-blue line-clamp-1">
                {item?.farmer_name}
              </p>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-medium">{item?.farmer_name}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      <div className="col-span-3 text-xs">
        {cell(t('id.number'), item?.number)}
      </div>
      <div className="col-span-4 text-xs">
        {cell(t('contract.number'), item?.contract_number)}
      </div>
      <div className="col-span-4 text-xs">
        {cell(t('contract.type'), item?.deal_type || '-')}
      </div>
    </NavLink>
  );
};
